<template>
    <div class="min-h-screen bg-slate-50/70">
        <div class="p-6 pb-0">
            <BreadCrumbs :navigation="navigation" />
        </div>

        <div class="p-6 space-y-6">

            <!-- Header -->
            <div class="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                <div class="px-6 py-6 bg-gradient-to-br from-white via-slate-50 to-amber-50">
                    <div class="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
                        <div class="flex items-start gap-4">
                            <span class="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-amber-100 text-amber-700 shadow-sm">
                                <v-icon name="hi-solid-bell" class="w-6 h-6" />
                            </span>
                            <div>
                                <h1 class="text-2xl font-bold tracking-tight text-slate-900">Promemoria</h1>
                                <p class="text-sm text-slate-500 mt-1 max-w-2xl">
                                    Gestisci i promemoria aziendali, smistali ai reparti e prendili in carico.
                                </p>
                            </div>
                        </div>

                        <button
                            @click="showCreateModal = true"
                            class="inline-flex items-center justify-center gap-2 bg-amber-500 text-white px-4 py-2.5 rounded-xl hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 text-sm font-semibold shadow-sm transition-colors"
                        >
                            <v-icon name="hi-plus" class="w-4 h-4" />
                            Nuovo promemoria
                        </button>
                    </div>

                    <!-- Stats -->
                    <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
                        <div class="bg-white/80 backdrop-blur border border-slate-200 rounded-2xl px-4 py-3 shadow-sm">
                            <p class="text-xs font-medium text-slate-500">Inbox generale</p>
                            <p class="text-2xl font-bold text-amber-600 mt-0.5">{{ localInbox.length }}</p>
                        </div>
                        <div class="bg-white/80 backdrop-blur border border-slate-200 rounded-2xl px-4 py-3 shadow-sm">
                            <p class="text-xs font-medium text-slate-500">In coda reparti</p>
                            <p class="text-2xl font-bold text-indigo-600 mt-0.5">{{ totalQueued }}</p>
                        </div>
                        <div class="bg-white/80 backdrop-blur border border-slate-200 rounded-2xl px-4 py-3 shadow-sm">
                            <p class="text-xs font-medium text-slate-500">Presi in carico</p>
                            <p class="text-2xl font-bold text-emerald-600 mt-0.5">{{ takenReminders.length }}</p>
                        </div>
                        <div class="bg-white/80 backdrop-blur border border-slate-200 rounded-2xl px-4 py-3 shadow-sm">
                            <p class="text-xs font-medium text-slate-500">Reparti attivi</p>
                            <p class="text-2xl font-bold text-slate-700 mt-0.5">{{ departments.length }}</p>
                        </div>
                    </div>
                </div>

                <!-- Tabs -->
                <div class="border-b border-slate-200 px-6">
                    <nav class="flex gap-1 -mb-px">
                        <button
                            v-for="tab in tabs"
                            :key="tab.key"
                            @click="activeTab = tab.key"
                            class="px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap"
                            :class="activeTab === tab.key
                                ? 'border-amber-500 text-amber-700'
                                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'"
                        >
                            {{ tab.label }}
                            <span
                                v-if="tab.count > 0"
                                class="ml-1.5 inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-xs font-bold"
                                :class="activeTab === tab.key ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'"
                            >
                                {{ tab.count }}
                            </span>
                        </button>
                    </nav>
                </div>
            </div>

            <!-- Tab: Inbox Generale -->
            <div v-if="activeTab === 'inbox'">
                <div v-if="localInbox.length === 0" class="bg-white border border-slate-200 rounded-2xl p-12 flex flex-col items-center text-slate-400 shadow-sm">
                    <v-icon name="hi-solid-inbox" class="w-12 h-12 mb-3 opacity-30" />
                    <p class="text-sm font-medium">Inbox vuota</p>
                    <p class="text-xs mt-1">I nuovi promemoria non assegnati appariranno qui.</p>
                </div>

                <div v-else class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    <div
                        v-for="reminder in localInbox"
                        :key="reminder.id"
                        class="bg-amber-50/70 border border-amber-200 rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow"
                        style="background: linear-gradient(135deg, #fef9c3 0%, #fef3c7 100%);"
                    >
                        <div class="flex items-start justify-between gap-2 mb-2">
                            <h3 class="font-semibold text-slate-900 leading-snug flex-1">{{ reminder.title }}</h3>
                            <span class="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                                Nuovo
                            </span>
                        </div>

                        <p v-if="reminder.description" class="text-xs text-slate-500 line-clamp-2 mb-3">
                            {{ reminder.description }}
                        </p>

                        <div class="flex items-center gap-1.5 text-xs text-slate-400 mb-3">
                            <v-icon name="hi-solid-user" class="w-3.5 h-3.5" />
                            <span>{{ reminder.creator?.name ?? '—' }}</span>
                            <span class="ml-auto">{{ formatDate(reminder.created_at) }}</span>
                        </div>

                        <div class="pt-3 border-t border-amber-200 space-y-2">
                            <div class="flex gap-2">
                                <select
                                    v-model="assignTargets[reminder.id]"
                                    class="flex-1 text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400"
                                >
                                    <option value="">Smista a reparto…</option>
                                    <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                                        {{ dept.name }}
                                    </option>
                                </select>
                                <button
                                    @click="assignDepartment(reminder)"
                                    :disabled="!assignTargets[reminder.id] || submittingId === reminder.id"
                                    class="px-3 py-1.5 rounded-lg text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                >
                                    Smista
                                </button>
                            </div>
                            <button
                                @click="take(reminder, 'inbox')"
                                :disabled="submittingId === reminder.id"
                                class="w-full py-1.5 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                                Prendi in carico
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Tab: Code Reparti -->
            <div v-if="activeTab === 'reparti'">
                <div v-if="departments.length === 0" class="bg-white border border-slate-200 rounded-2xl p-12 flex flex-col items-center text-slate-400 shadow-sm">
                    <v-icon name="hi-solid-user-group" class="w-12 h-12 mb-3 opacity-30" />
                    <p class="text-sm font-medium">Nessun reparto attivo</p>
                </div>

                <div v-else class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    <DepartmentQueueWidget
                        v-for="dept in departments"
                        :key="dept.id"
                        :department="dept"
                        :queue="localQueues[dept.id] || []"
                        :taking-id="submittingId"
                        @take="(reminder) => take(reminder, 'queue', dept.id)"
                    />
                </div>
            </div>

            <!-- Tab: Presi in Carico -->
            <div v-if="activeTab === 'presi'">
                <div v-if="localTaken.length === 0" class="bg-white border border-slate-200 rounded-2xl p-12 flex flex-col items-center text-slate-400 shadow-sm">
                    <v-icon name="hi-solid-check-circle" class="w-12 h-12 mb-3 opacity-30" />
                    <p class="text-sm font-medium">Nessun promemoria preso in carico</p>
                </div>

                <div v-else class="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                    <div class="divide-y divide-slate-100">
                        <div
                            v-for="reminder in localTaken"
                            :key="reminder.id"
                            class="flex items-start gap-4 px-5 py-4 hover:bg-slate-50 transition-colors"
                        >
                            <span class="flex-shrink-0 mt-0.5 w-8 h-8 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
                                <v-icon name="hi-solid-check" class="w-4 h-4" />
                            </span>
                            <div class="flex-1 min-w-0">
                                <p class="text-sm font-semibold text-slate-800">{{ reminder.title }}</p>
                                <p v-if="reminder.description" class="text-xs text-slate-400 mt-0.5 line-clamp-1">{{ reminder.description }}</p>
                                <div class="flex items-center gap-3 mt-1.5 flex-wrap">
                                    <span class="text-xs text-slate-400">
                                        <v-icon name="hi-solid-user" class="w-3 h-3 inline mr-0.5" />
                                        {{ reminder.creator?.name ?? '—' }}
                                    </span>
                                    <span v-if="reminder.department" class="text-xs text-indigo-600 font-medium">
                                        {{ reminder.department.name }}
                                    </span>
                                    <span class="text-xs text-slate-400 ml-auto">
                                        Preso in carico {{ formatDate(reminder.converted_at) }}
                                    </span>
                                </div>
                            </div>
                            <span class="flex-shrink-0 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                In carico
                            </span>
                        </div>
                    </div>
                </div>
            </div>

        </div>

        <CreateReminderModal
            :visible="showCreateModal"
            :departments="departments"
            :customers="customers"
            @close="showCreateModal = false"
            @created="onCreated"
        />
    </div>
</template>

<script>
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import CreateReminderModal from './Components/CreateReminderModal.vue';
import DepartmentQueueWidget from './Components/DepartmentQueueWidget.vue';

export default {
    name: 'PromemoriaIndex',

    components: { BreadCrumbs, CreateReminderModal, DepartmentQueueWidget },

    props: {
        generalInbox: { type: Array, default: () => [] },
        departments:  { type: Array, default: () => [] },
        departmentQueues: { type: Object, default: () => ({}) },
        takenReminders: { type: Array, default: () => [] },
        customers: { type: Array, default: () => [] },
    },

    data() {
        return {
            activeTab: 'inbox',
            showCreateModal: false,
            submittingId: null,
            assignTargets: {},
            localInbox: [...this.generalInbox],
            localQueues: { ...this.departmentQueues },
            localTaken: [...this.takenReminders],
            navigation: [
                { name: 'Dashboard', href: route('dashboard') },
                { name: 'Attività', href: route('modules.home', 'attivita') },
                { name: 'Promemoria', href: route('modules.attivita.reminders') },
            ],
        };
    },

    computed: {
        totalQueued() {
            return Object.values(this.localQueues).reduce((sum, q) => sum + (q?.length ?? 0), 0);
        },

        tabs() {
            return [
                { key: 'inbox',  label: 'Inbox generale',    count: this.localInbox.length },
                { key: 'reparti', label: 'Code reparti',     count: this.totalQueued },
                { key: 'presi',  label: 'Presi in carico',   count: this.localTaken.length },
            ];
        },
    },

    methods: {
        formatDate(value) {
            if (!value) return '—';
            return new Date(value).toLocaleDateString('it-IT', {
                day: '2-digit', month: '2-digit', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
            });
        },

        assignDepartment(reminder) {
            const departmentId = this.assignTargets[reminder.id];
            if (!departmentId || this.submittingId) return;

            this.submittingId = reminder.id;

            axios.patch(
                route('modules.attivita.promemoria.assegna-reparto', { reminder: reminder.id }),
                { department_id: departmentId }
            )
                .then((response) => {
                    this.localInbox = this.localInbox.filter(r => r.id !== reminder.id);

                    if (this.localQueues[departmentId]) {
                        this.localQueues[departmentId] = [response.data.reminder, ...this.localQueues[departmentId]];
                    } else {
                        this.localQueues = { ...this.localQueues, [departmentId]: [response.data.reminder] };
                    }
                })
                .catch(() => {
                    alert('Errore durante l\'assegnazione. Riprova.');
                })
                .finally(() => {
                    this.submittingId = null;
                });
        },

        take(reminder, source, deptId = null) {
            if (this.submittingId) return;

            this.submittingId = reminder.id;

            axios.post(route('modules.attivita.promemoria.prendi-in-carico', { reminder: reminder.id }))
                .then((response) => {
                    if (source === 'inbox') {
                        this.localInbox = this.localInbox.filter(r => r.id !== reminder.id);
                    } else if (source === 'queue' && deptId) {
                        this.localQueues = {
                            ...this.localQueues,
                            [deptId]: (this.localQueues[deptId] || []).filter(r => r.id !== reminder.id),
                        };
                    }

                    this.localTaken = [response.data.reminder, ...this.localTaken];
                    this.activeTab = 'presi';
                })
                .catch(() => {
                    alert('Errore durante la presa in carico. Riprova.');
                })
                .finally(() => {
                    this.submittingId = null;
                });
        },

        onCreated(newReminder) {
            this.showCreateModal = false;

            if (!newReminder) return;

            if (!newReminder.department_id) {
                this.localInbox = [newReminder, ...this.localInbox];
            } else {
                const deptId = newReminder.department_id;
                this.localQueues = {
                    ...this.localQueues,
                    [deptId]: [newReminder, ...(this.localQueues[deptId] || [])],
                };
                this.activeTab = 'reparti';
            }
        },
    },
};
</script>
